import { Button, Icon, Modal, Tooltip, useToast } from '@lego/klik-ui';
import { PlusBold } from '@lego/klik-ui/icons';
import { useCallback, useMemo, useState } from 'react';
import { AddSshKeyForm } from '@/components/device/AddSshKeyForm';
import { useDeleteSshKey, useSshKeys } from '@/hooks/device_ssh_keys';
import { ToastOptions } from '@/utility/ToastOptions';
import { sshKeysTableColumns } from '../common/table/column_definitions/SshKeys';
import { TableStoreProvider } from '../common/table/store/Context';
import { Table } from '../common/table/Table';

type Props = {
  deviceId: number;
};

const sshKeysDescription = 'SSH keys associated with this device';

export const DeviceSshKeys = ({ deviceId }: Props) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const toast = useToast();
  const { data, isLoading, isValidating, mutate } = useSshKeys(deviceId);

  const deleteSshKey = useDeleteSshKey(deviceId, () => {
    console.error('Failed to delete ssh key');
  });

  const handleDelete = useCallback(
    async (sshKeyId: number) => {
      try {
        await deleteSshKey(sshKeyId);
        await mutate();
        toast(
          ToastOptions.success(
            'SSH Key Removed',
            'The SSH key has been removed from the device.',
            3000
          )
        );
      } catch {
        // Already toasted and logged by the mutation hook; caught only to
        // avoid an unhandled promise rejection here.
      }
    },
    [deleteSshKey, mutate, toast]
  );

  const handleAddSuccess = useCallback(async () => {
    setIsAddModalOpen(false);
    await mutate();
  }, [mutate]);

  const columns = useMemo(() => sshKeysTableColumns(handleDelete), [handleDelete]);

  return (
    <TableStoreProvider initialSorting={[{ desc: true, id: 'id' }]} name="DeviceSshKeys">
      <Table
        addButton={() => (
          <Tooltip label="Add a new SSH key" placement="left">
            <Button
              borderRightRadius="0px"
              leftIcon={<Icon as={PlusBold} />}
              onClick={() => setIsAddModalOpen(true)}
              size="sm"
              variant="outline"
            >
              Add
            </Button>
          </Tooltip>
        )}
        columns={columns}
        count={data?.itemCount || 0}
        data={data?.deviceSSHKeys || []}
        description={sshKeysDescription}
        isLoading={Boolean(isLoading || !data)}
        isValidating={isValidating}
        refresh={mutate}
        title="SSH Keys"
      />
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>Add SSH Key to Device</Modal.Header>
          <Modal.Body pb={6}>
            <AddSshKeyForm
              deviceId={deviceId}
              onCancel={() => setIsAddModalOpen(false)}
              onSuccess={handleAddSuccess}
            />
          </Modal.Body>
        </Modal.Content>
      </Modal>
    </TableStoreProvider>
  );
};
